import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiPost, formatFecha } from '../shared/ui.tsx';
import { LARGO_MINIMO_CLAVE, MedidorClave } from '../shared/MedidorClave.tsx';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface InfoInvitacion {
  nombre: string;
  apellido: string;
  expira_en: string;
}

export function OnboardingPage(): ReactNode {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [info, setInfo] = useState<InfoInvitacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // cargar info de la invitación
  if (cargando && !info && !errorCarga) {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/invitaciones/v/${token}`);
        const d = (await r.json()) as { nombre?: string; apellido?: string; expira_en?: string; error?: string };
        if (!r.ok) throw new Error(d.error ?? 'Error');
        setInfo({ nombre: d.nombre ?? '', apellido: d.apellido ?? '', expira_en: d.expira_en ?? '' });
      } catch (e) {
        setErrorCarga(e instanceof Error ? e.message : 'Error al cargar la invitación');
      } finally {
        setCargando(false);
      }
    })();
  }

  async function aceptar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== password2) { setError('Las contraseñas no coinciden'); return; }
    if (password.length < LARGO_MINIMO_CLAVE) { setError(`Mínimo ${LARGO_MINIMO_CLAVE} caracteres`); return; }
    setEnviando(true);
    try {
      await apiPost('/api/invitaciones/aceptar', { token, email, password });
      navigate('/login', { replace: true, state: { msg: 'Cuenta creada. Ingresá con tu email.' } });
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : 'Error');
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-neutro-700">Cargando invitación…</p>
      </div>
    );
  }
  if (errorCarga) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <div className="max-w-sm rounded-2xl bg-red-50 p-6 text-center text-estado-error">
          <p className="font-semibold">Invitación inválida</p>
          <p className="mt-1 text-sm">{errorCarga}</p>
          <button className="mt-4 text-sm underline" onClick={() => navigate('/login')}>Volver al login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-superficie-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-5 text-center">
          <h1 className="text-2xl font-bold text-neutro-800">Bienvenido a CONVOCA</h1>
          <p className="text-sm text-neutro-500">
            Hola <span className="font-semibold">{info?.nombre} {info?.apellido}</span>, creá tu cuenta para acceder a la app.
          </p>
        </div>

        <form onSubmit={aceptar} className="space-y-4 rounded-2xl bg-white p-6 shadow-xl border border-contorno-var">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Email</span>
            <input className="campo" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Contraseña (mín. {LARGO_MINIMO_CLAVE})</span>
            <input className="campo" type="password" required minLength={LARGO_MINIMO_CLAVE} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <MedidorClave password={password} />
            <span className="mt-1 block text-xs text-neutro-400">Sin reglas raras: una frase larga que solo vos recuerdes. No sirven las claves comunes ni las filtradas.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Repetir contraseña</span>
            <input className="campo" type="password" required minLength={LARGO_MINIMO_CLAVE} autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </label>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{error}</p>}

          <button type="submit" disabled={enviando} className="boton w-full bg-primario-400 text-white hover:bg-primario-500">
            {enviando ? 'Creando…' : 'Crear cuenta y entrar'}
          </button>
        </form>

        {info?.expira_en && (
          <p className="mt-3 text-center text-xs text-neutro-400">
            La invitación vence el {formatFecha(info.expira_en)}.
          </p>
        )}
      </div>
    </div>
  );
}